import { Component, computed, inject, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanySalaryTwoService } from '../../../../../services/companies/company-salary-two.service';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { LutService } from '../../../../../services/lut.service';
import { Currency } from '../../../../../models/currency';
import { CompensationMultiplier } from '../../../../../models/compensationMultiplier';
import { CompensationStandard } from '../../../../../models/compensationStandard';
import { CompensationConsultantFirm } from '../../../../../models/consultantFirm';
import {
  SalaryTwoResponseDto,
  SalaryTwoUpsertRequest,
} from '../../../../../models/api/companySalaryTwoDto';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-salary2-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatAutocompleteModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './salary2-tab.component.html',
  styleUrl: './salary2-tab.component.scss'
})
export class Salary2TabComponent implements OnInit, OnDestroy, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private salaryTwoService = inject(CompanySalaryTwoService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private lutService = inject(LutService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Salary2));

  currencies: Currency[] = [];
  multipliers: CompensationMultiplier[] = [];
  standards: CompensationStandard[] = [];
  consultantFirms: CompensationConsultantFirm[] = [];
  filteredConsultantFirms1: CompensationConsultantFirm[] = [];
  filteredConsultantFirms2: CompensationConsultantFirm[] = [];
  consultantFirm1SearchControl = new FormControl('');
  consultantFirm2SearchControl = new FormControl('');
  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  otherMeasurement: string = '';
  otherData: SalaryTwoResponseDto | null = null;
  companyId: string | null = null;
  showYearWarning = false;
  isStandardsCarriedForward = false;
  showKeywords = true;
  totalFees1AutoFilled = false;
  totalFees2AutoFilled = false;
  private destroy$ = new Subject<void>();

  nonExecutiveFeeRows = [
    { label: 'Chair', field: 'chairFees' },
    { label: 'Vice Chair', field: 'viceChairFees' },
    { label: 'Annual Retainer-Cash', field: 'annualRetainerCashFees' },
    { label: 'Annual Retainer-Equity', field: 'annualRetainerEquityFees' },
    { label: 'Senior Independent', field: 'seniorIndependentFees' },
    { label: 'Audit Committee Chair', field: 'auditCommitteeChairFees' },
    { label: 'Remuneration Committee Chair', field: 'remunerationCommitteeChairFees' },
    { label: 'Nomination Committee Chair', field: 'nominationCommitteeChairFees' },
    { label: 'Other Committee Chair', field: 'otherCommitteeChairFees' },
    { label: 'Audit Committee Member', field: 'auditCommitteeMemberFees' },
    { label: 'Remuneration Committee Member', field: 'remunerationCommitteeMemberFees' },
    { label: 'Nomination Committee Member', field: 'nominationCommitteeMemberFees' },
    { label: 'Other Committee Member', field: 'otherCommitteeMemberFees' },
    { label: 'Expense Allowance', field: 'expenseAllowanceFees' },
    { label: 'Fees per Meeting', field: 'feesPerMeeting' },
    { label: 'Fees for Telephone Meetings', field: 'feesForTelephoneMeetings' },
  ];

  compensationSummaryRows = [
    { label: 'CEO', prefix: 'ceo' },
    { label: 'CFO', prefix: 'cfo' },
    { label: 'Other Executives', prefix: 'otherExecutives' },
    { label: 'Executive Management', prefix: 'executiveManagement' },
    { label: 'Non-Executive Directors', prefix: 'nonExecutiveDirectors' },
  ];

  form: FormGroup = this.fb.group({
    ceoMultiplierId: [null as string | null],
    ceoStandardId: [null as string | null],
    ceoAmount: [null as number | null],
    cfoMultiplierId: [null as string | null],
    cfoStandardId: [null as string | null],
    cfoAmount: [null as number | null],
    otherExecutivesMultiplierId: [null as string | null],
    otherExecutivesStandardId: [null as string | null],
    otherExecutivesAmount: [null as number | null],
    executiveManagementMultiplierId: [null as string | null],
    executiveManagementStandardId: [null as string | null],
    executiveManagementAmount: [null as number | null],
    nonExecutiveDirectorsMultiplierId: [null as string | null],
    nonExecutiveDirectorsStandardId: [null as string | null],
    nonExecutiveDirectorsAmount: [null as number | null],
    chairFees: [null as number | null],
    viceChairFees: [null as number | null],
    annualRetainerCashFees: [null as number | null],
    annualRetainerEquityFees: [null as number | null],
    seniorIndependentFees: [null as number | null],
    auditCommitteeChairFees: [null as number | null],
    remunerationCommitteeChairFees: [null as number | null],
    nominationCommitteeChairFees: [null as number | null],
    otherCommitteeChairFees: [null as number | null],
    auditCommitteeMemberFees: [null as number | null],
    remunerationCommitteeMemberFees: [null as number | null],
    nominationCommitteeMemberFees: [null as number | null],
    otherCommitteeMemberFees: [null as number | null],
    expenseAllowanceFees: [null as number | null],
    feesPerMeeting: [null as number | null],
    feesForTelephoneMeetings: [null as number | null],
    consultantFirm1Id: [null as string | null],
    consultantName1: [''],
    compensationFees1: [null as number | null],
    otherFees1: [null as number | null],
    totalFees1: [null as number | null],
    consultantFirm2Id: [null as string | null],
    consultantName2: [''],
    compensationFees2: [null as number | null],
    otherFees2: [null as number | null],
    totalFees2: [null as number | null],
    ceoPayRatioMediaEmployee: [null as number | null],
    ceoPayRatioRatio: [null as number | null],
    ceoPayRatioNumberOfEmployees: [null as number | null],
    ownershipGuideline1: [''],
    ownershipGuideline2: [''],
    compensationConsultantComment3: [''],
    compensationConsultantComment4: [''],
    nonExecutiveFeesComment5: [''],
    comments2: [''],
    keywords: this.fb.group({
      ownershipGuidelinesOne: [''],
      ownershipGuidelinesTwo: [''],
      nonExecutiveFeesOne: [''],
      nonExecutiveFeesTwo: [''],
      compensationConsultantOne: [''],
      compensationConsultantTwo: ['']
    })
  });

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('salary2'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'salary2', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'salary2', year: this.selectedYear }));
    }

    this.patchKeywords();

    this.lutService.getCurrencies().subscribe(data => {
      this.currencies = data.sort((a, b) => a.name.localeCompare(b.name));
    });
    this.lutService.getCompensationMultipliers().subscribe(data => {
      this.multipliers = data.sort((a, b) => {
        const pa = a.priority ? 0 : 1;
        const pb = b.priority ? 0 : 1;
        if (pa !== pb) return pa - pb;
        const nameA = a.name ?? String(a.value);
        const nameB = b.name ?? String(b.value);
        return nameA.localeCompare(nameB);
      });
    });
    this.lutService.getCompensationStandards().subscribe(data => {
      this.standards = data.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    });
    this.lutService.getConsultantFirms().subscribe(data => {
      this.consultantFirms = data.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      this.syncConsultantFirmSearchControls();
    });

    this.consultantFirm1SearchControl.valueChanges.subscribe(val => {
      this.filteredConsultantFirms1 = this.filterConsultantFirms(val);
    });
    this.consultantFirm2SearchControl.valueChanges.subscribe(val => {
      this.filteredConsultantFirms2 = this.filterConsultantFirms(val);
    });

    // The consultant-firm autocompletes are standalone controls outside `form`, so
    // ReadOnlyTabDirective (which disables the form group) does not reach them — lock them here.
    if (this.isReadOnly()) {
      this.consultantFirm1SearchControl.disable({ emitEvent: false });
      this.consultantFirm2SearchControl.disable({ emitEvent: false });
    }

    this.setupTotalFeesAutoCalc();

    this.loadData();
  }

  private setupTotalFeesAutoCalc(): void {
    ([1, 2] as const).forEach(idx => {
      const cf = this.form.get(`compensationFees${idx}`);
      const other = this.form.get(`otherFees${idx}`);
      const tf = this.form.get(`totalFees${idx}`);
      if (!cf || !other || !tf) return;

      const recompute = () => {
        const cfVal = cf.value;
        const otherVal = other.value;
        const newTotal = (cfVal == null && otherVal == null)
          ? null
          : Number(cfVal ?? 0) + Number(otherVal ?? 0);
        const valueChanged = tf.value !== newTotal;
        tf.setValue(newTotal, { emitEvent: false });
        tf.markAsDirty();
        if (valueChanged && newTotal != null) {
          if (idx === 1) {
            this.totalFees1AutoFilled = true;
            setTimeout(() => this.totalFees1AutoFilled = false, 2000);
          } else {
            this.totalFees2AutoFilled = true;
            setTimeout(() => this.totalFees2AutoFilled = false, 2000);
          }
        }
      };

      cf.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(recompute);
      other.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(recompute);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private patchKeywords(): void {
    const ogOne = this.keywords.find(k => k.type === KeywordType.SalaryTwoOwnershipGuidelinesOne);
    const ogTwo = this.keywords.find(k => k.type === KeywordType.SalaryTwoOwnershipGuidelinesTwo);
    const nefOne = this.keywords.find(k => k.type === KeywordType.SalaryTwoNonExecutiveFeesOne);
    const nefTwo = this.keywords.find(k => k.type === KeywordType.SalaryTwoNonExecutiveFeesTwo);
    const ccOne = this.keywords.find(k => k.type === KeywordType.SalaryTwoCompensationConsultantOne);
    const ccTwo = this.keywords.find(k => k.type === KeywordType.SalaryTwoCompensationConsultantTwo);
    this.form.get('keywords')!.patchValue({
      ownershipGuidelinesOne: ogOne?.value ?? '',
      ownershipGuidelinesTwo: ogTwo?.value ?? '',
      nonExecutiveFeesOne: nefOne?.value ?? '',
      nonExecutiveFeesTwo: nefTwo?.value ?? '',
      compensationConsultantOne: ccOne?.value ?? '',
      compensationConsultantTwo: ccTwo?.value ?? ''
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch', 'Stay', 'swap_horiz', 'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasSalaryChanges = this.form.dirty && !hasKeywordChanges ? true :
      this.form.get('comments2')!.dirty ||
      ['ceoMultiplierId', 'ceoStandardId', 'ceoAmount', 'chairFees', 'consultantFirm1Id'].some(f => this.form.get(f)?.dirty);

    if (hasSalaryChanges && hasKeywordChanges) return 'salary data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'salary data';
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.salaryTwoService.getSalaryTwo(this.companyId, parseInt(year)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => { this.otherData = null; }
    });
  }

  getOtherField(field: string): number | null | undefined {
    if (!this.otherData) return undefined;
    return (this.otherData as any)[field];
  }

  getOtherStringField(field: string): string | null | undefined {
    if (!this.otherData) return undefined;
    return (this.otherData as any)[field];
  }

  isDifferent(a: any, b: any): boolean {
    return a !== b && (a != null || b != null);
  }

  formatOther(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  formatRequirementMet(value: boolean | null | undefined): string {
    if (value == null) return '';
    return value ? 'Yes' : 'No';
  }

  getMultiplierDisplay(id: string | null | undefined): string {
    if (!id) return '';
    const m = this.multipliers.find(x => x.id === id);
    return m ? String(m.value) : '';
  }

  private syncConsultantFirmSearchControls(): void {
    if (this.consultantFirms.length === 0) return;
    const firm1Id = this.form.get('consultantFirm1Id')?.value;
    const firm2Id = this.form.get('consultantFirm2Id')?.value;
    const firm1Name = firm1Id ? (this.consultantFirms.find(f => f.id === firm1Id)?.name ?? '') : '';
    const firm2Name = firm2Id ? (this.consultantFirms.find(f => f.id === firm2Id)?.name ?? '') : '';
    this.consultantFirm1SearchControl.setValue(firm1Name, { emitEvent: false });
    this.consultantFirm2SearchControl.setValue(firm2Name, { emitEvent: false });
  }

  filterConsultantFirms(searchText: string | CompensationConsultantFirm | null): CompensationConsultantFirm[] {
    if (!searchText || typeof searchText !== 'string' || searchText.length < 2) return [];
    const filter = searchText.toLowerCase();
    return this.consultantFirms.filter(f => f.name.toLowerCase().includes(filter)).slice(0, 100);
  }

  onConsultantFirmSelected(firmIndex: 1 | 2, firm: CompensationConsultantFirm): void {
    const controlName = firmIndex === 1 ? 'consultantFirm1Id' : 'consultantFirm2Id';
    const searchControl = firmIndex === 1 ? this.consultantFirm1SearchControl : this.consultantFirm2SearchControl;
    this.form.get(controlName)!.setValue(firm.id);
    searchControl.setValue(firm.name, { emitEvent: false });
    this.form.get(controlName)!.markAsDirty();
    this.form.markAsDirty();
  }

  clearConsultantFirm(firmIndex: 1 | 2): void {
    const controlName = firmIndex === 1 ? 'consultantFirm1Id' : 'consultantFirm2Id';
    const searchControl = firmIndex === 1 ? this.consultantFirm1SearchControl : this.consultantFirm2SearchControl;
    this.form.get(controlName)!.setValue(null);
    searchControl.setValue('');
    this.form.get(controlName)!.markAsDirty();
    this.form.markAsDirty();
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'salary2', year }));
    this.loadData();
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.salaryTwoService.getSalaryTwo(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const formValues = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isStandardsCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isStandardsCarriedForward || resource.state === 'absent';
        if (formValues) {
          this.populateForm(formValues);
        } else {
          this.resetForm();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        this.isStandardsCarriedForward = false;
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading salary data');
        }
        console.error('Salary 2 data error:', error);
      }
    });
  }

  private populateForm(response: SalaryTwoResponseDto): void {
    this.form.patchValue({
      ceoMultiplierId: response.ceoMultiplierId,
      ceoStandardId: response.ceoStandardId,
      ceoAmount: response.ceoAmount,
      cfoMultiplierId: response.cfoMultiplierId,
      cfoStandardId: response.cfoStandardId,
      cfoAmount: response.cfoAmount,
      otherExecutivesMultiplierId: response.otherExecutivesMultiplierId,
      otherExecutivesStandardId: response.otherExecutivesStandardId,
      otherExecutivesAmount: response.otherExecutivesAmount,
      executiveManagementMultiplierId: response.executiveManagementMultiplierId,
      executiveManagementStandardId: response.executiveManagementStandardId,
      executiveManagementAmount: response.executiveManagementAmount,
      nonExecutiveDirectorsMultiplierId: response.nonExecutiveDirectorsMultiplierId,
      nonExecutiveDirectorsStandardId: response.nonExecutiveDirectorsStandardId,
      nonExecutiveDirectorsAmount: response.nonExecutiveDirectorsAmount,
      chairFees: response.chairFees,
      viceChairFees: response.viceChairFees,
      annualRetainerCashFees: response.annualRetainerCashFees,
      annualRetainerEquityFees: response.annualRetainerEquityFees,
      seniorIndependentFees: response.seniorIndependentFees,
      auditCommitteeChairFees: response.auditCommitteeChairFees,
      remunerationCommitteeChairFees: response.remunerationCommitteeChairFees,
      nominationCommitteeChairFees: response.nominationCommitteeChairFees,
      otherCommitteeChairFees: response.otherCommitteeChairFees,
      auditCommitteeMemberFees: response.auditCommitteeMemberFees,
      remunerationCommitteeMemberFees: response.remunerationCommitteeMemberFees,
      nominationCommitteeMemberFees: response.nominationCommitteeMemberFees,
      otherCommitteeMemberFees: response.otherCommitteeMemberFees,
      expenseAllowanceFees: response.expenseAllowanceFees,
      feesPerMeeting: response.feesPerMeeting,
      feesForTelephoneMeetings: response.feesForTelephoneMeetings,
      consultantFirm1Id: response.consultantFirm1Id,
      consultantName1: response.consultantName1 ?? '',
      compensationFees1: response.compensationFees1,
      otherFees1: response.otherFees1,
      totalFees1: response.totalFees1,
      consultantFirm2Id: response.consultantFirm2Id,
      consultantName2: response.consultantName2 ?? '',
      compensationFees2: response.compensationFees2,
      otherFees2: response.otherFees2,
      totalFees2: response.totalFees2,
      ceoPayRatioMediaEmployee: response.ceoPayRatioMediaEmployee,
      ceoPayRatioRatio: response.ceoPayRatioRatio,
      ceoPayRatioNumberOfEmployees: response.ceoPayRatioNumberOfEmployees,
      comments2: response.comments2 ?? '',
    }, { emitEvent: false });

    this.syncConsultantFirmSearchControls();

    this.form.markAsPristine();
  }

  private resetForm(): void {
    const keywordsValue = this.form.get('keywords')!.value;
    this.form.reset();
    this.form.get('keywords')!.patchValue(keywordsValue, { emitEvent: false });
    this.consultantFirm1SearchControl.setValue('', { emitEvent: false });
    this.consultantFirm2SearchControl.setValue('', { emitEvent: false });
    this.form.markAsPristine();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const f = this.form.value;

    const topLevelFields = [
      'ceoMultiplierId', 'ceoStandardId', 'ceoAmount',
      'cfoMultiplierId', 'cfoStandardId', 'cfoAmount',
      'otherExecutivesMultiplierId', 'otherExecutivesStandardId', 'otherExecutivesAmount',
      'executiveManagementMultiplierId', 'executiveManagementStandardId', 'executiveManagementAmount',
      'nonExecutiveDirectorsMultiplierId', 'nonExecutiveDirectorsStandardId', 'nonExecutiveDirectorsAmount',
      'chairFees', 'viceChairFees', 'annualRetainerCashFees', 'annualRetainerEquityFees',
      'seniorIndependentFees', 'auditCommitteeChairFees', 'remunerationCommitteeChairFees',
      'nominationCommitteeChairFees', 'otherCommitteeChairFees', 'auditCommitteeMemberFees',
      'remunerationCommitteeMemberFees', 'nominationCommitteeMemberFees', 'otherCommitteeMemberFees',
      'expenseAllowanceFees', 'feesPerMeeting', 'feesForTelephoneMeetings',
      'consultantFirm1Id', 'consultantName1', 'compensationFees1', 'otherFees1', 'totalFees1',
      'consultantFirm2Id', 'consultantName2', 'compensationFees2', 'otherFees2', 'totalFees2',
      'ceoPayRatioMediaEmployee', 'ceoPayRatioRatio', 'ceoPayRatioNumberOfEmployees',
      'comments2',
    ];
    const topLevelDirty = this.isStandardsCarriedForward || topLevelFields.some(field => this.form.get(field)?.dirty);

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, [
      { formField: 'ownershipGuidelinesOne', type: KeywordType.SalaryTwoOwnershipGuidelinesOne },
      { formField: 'ownershipGuidelinesTwo', type: KeywordType.SalaryTwoOwnershipGuidelinesTwo },
      { formField: 'nonExecutiveFeesOne', type: KeywordType.SalaryTwoNonExecutiveFeesOne },
      { formField: 'nonExecutiveFeesTwo', type: KeywordType.SalaryTwoNonExecutiveFeesTwo },
      { formField: 'compensationConsultantOne', type: KeywordType.SalaryTwoCompensationConsultantOne },
      { formField: 'compensationConsultantTwo', type: KeywordType.SalaryTwoCompensationConsultantTwo }
    ], this.keywords);

    const hasSalaryData = topLevelDirty;
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => k.type === KeywordType.SalaryTwoOwnershipGuidelinesOne ||
           k.type === KeywordType.SalaryTwoOwnershipGuidelinesTwo ||
           k.type === KeywordType.SalaryTwoNonExecutiveFeesOne ||
           k.type === KeywordType.SalaryTwoNonExecutiveFeesTwo ||
           k.type === KeywordType.SalaryTwoCompensationConsultantOne ||
           k.type === KeywordType.SalaryTwoCompensationConsultantTwo
    );

    if (!hasSalaryData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();
    const request: SalaryTwoUpsertRequest = {
      year: parseInt(this.selectedYear),
      ceoMultiplierId: f.ceoMultiplierId,
      ceoStandardId: f.ceoStandardId,
      ceoAmount: f.ceoAmount,
      cfoMultiplierId: f.cfoMultiplierId,
      cfoStandardId: f.cfoStandardId,
      cfoAmount: f.cfoAmount,
      otherExecutivesMultiplierId: f.otherExecutivesMultiplierId,
      otherExecutivesStandardId: f.otherExecutivesStandardId,
      otherExecutivesAmount: f.otherExecutivesAmount,
      executiveManagementMultiplierId: f.executiveManagementMultiplierId,
      executiveManagementStandardId: f.executiveManagementStandardId,
      executiveManagementAmount: f.executiveManagementAmount,
      nonExecutiveDirectorsMultiplierId: f.nonExecutiveDirectorsMultiplierId,
      nonExecutiveDirectorsStandardId: f.nonExecutiveDirectorsStandardId,
      nonExecutiveDirectorsAmount: f.nonExecutiveDirectorsAmount,
      chairFees: f.chairFees,
      viceChairFees: f.viceChairFees,
      annualRetainerCashFees: f.annualRetainerCashFees,
      annualRetainerEquityFees: f.annualRetainerEquityFees,
      seniorIndependentFees: f.seniorIndependentFees,
      auditCommitteeChairFees: f.auditCommitteeChairFees,
      remunerationCommitteeChairFees: f.remunerationCommitteeChairFees,
      nominationCommitteeChairFees: f.nominationCommitteeChairFees,
      otherCommitteeChairFees: f.otherCommitteeChairFees,
      auditCommitteeMemberFees: f.auditCommitteeMemberFees,
      remunerationCommitteeMemberFees: f.remunerationCommitteeMemberFees,
      nominationCommitteeMemberFees: f.nominationCommitteeMemberFees,
      otherCommitteeMemberFees: f.otherCommitteeMemberFees,
      expenseAllowanceFees: f.expenseAllowanceFees,
      feesPerMeeting: f.feesPerMeeting,
      feesForTelephoneMeetings: f.feesForTelephoneMeetings,
      consultantFirm1Id: f.consultantFirm1Id,
      consultantName1: f.consultantName1,
      compensationFees1: f.compensationFees1,
      otherFees1: f.otherFees1,
      totalFees1: f.totalFees1,
      consultantFirm2Id: f.consultantFirm2Id,
      consultantName2: f.consultantName2,
      compensationFees2: f.compensationFees2,
      otherFees2: f.otherFees2,
      totalFees2: f.totalFees2,
      ceoPayRatioMediaEmployee: f.ceoPayRatioMediaEmployee,
      ceoPayRatioRatio: f.ceoPayRatioRatio,
      ceoPayRatioNumberOfEmployees: f.ceoPayRatioNumberOfEmployees,
      comments2: f.comments2,
      boardMembers: [],
      executiveMembers: [],
    };

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasSalaryData
      ? this.salaryTwoService.updateSalaryTwo(this.companyId!, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_salaryResult, keywordsResult]) => {
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
        if (hasSalaryData) {
          this.loadData();
        }
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }
}
